/* eslint-disable react/jsx-one-expression-per-line */
import { useConditionalEffect, usePrevious } from 'react-delta';
import produce from 'immer';
import { useRecoilState, useSetRecoilState } from 'recoil';
import useLRQ, {
  ErrorResult,
  INITIAL_RESULT,
  LRQRequest,
  SuccessResult,
} from 'hooks/useLRQ/useLRQ';
import { eventCacheWatcher, lrqErrorsById } from 'api/apolloClient.state';
import * as K from 'common/constants';
import { useCoordinatedCancellation, useLRQIndication } from 'components/common/Managers/LRQRequestManager.hooks';

import './LRQRequestManager.scss';

/**
 * This component tracks and manages requests for Long Running Queries. Successful results are read directly from the
 * Apollo cache by UI components with the {@link useEventCache} hook; this component can ignore them. On the other hand,
 * this component tracks error responses, stores them in global state by query id, and clears them after subsequent
 * successful responses.
 *
 * @param request                Long running request comprising one or more event queries to be passed to useLRQ
 * @param showProgress           Defines if LRQ indicator is displayed in the UI.
 *                                 current limitations:
 *                                   1. No multiple LRQ indicators
 *                                   2. No LRQ indicator + another PageAlert (e.g. error)
 * @param launchDashboard        True if this should use the dedicated dashboard LRQ query interface
 * @param coordinatedRequests    If supplied, we watch all of these requests for cancellation, and cancel this request
 */

type LRQRequestManagerProps = {
  request: LRQRequest;
  showProgress?: boolean;
  launchDashboard?: boolean;
  coordinatedRequests?: LRQRequest[];
};

function LRQRequestManager({
  request,
  showProgress = false,
  launchDashboard = false,
  coordinatedRequests,
}: LRQRequestManagerProps): JSX.Element {

  const setEventCacheWatcher = useSetRecoilState(eventCacheWatcher);
  const [ errorsById, setErrorsById ] = useRecoilState(lrqErrorsById);
  const { result, cancel } = useLRQ(request, { launchDashboardQuery: launchDashboard });
  const prevResult = usePrevious(result);
  let queryIds: string[];
  // Query variables for launch Dashboard is an entirely separate type defined by  the server,
  // TS needs us to be explicit in which type of list we are mapping over.
  if (launchDashboard) {
    queryIds = (request?.dashboardQueriesVariables || []).map(({ id }) => id);
  } else {
    queryIds = (request?.queriesVariables || []).map(({ id }) => id);
  }

  /* Ensure that coordinated requests are cancelled together */
  useCoordinatedCancellation(request, cancel, coordinatedRequests);

  /* Visualize LRQ progress */
  useLRQIndication({ result, cancel }, request, showProgress);

  useConditionalEffect(() => {
    if ((result as ErrorResult).status === K.QueryStatus.Terminated) {
      // Terminated status on the result means that there was a top-level error and no usable data. Since these
      // top-level errors are currently "all or nothing", but consumer components expect errors at the individual query
      // level, we set the error state to each query, even though it might not apply to all of them.
      // TODO: {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-4398}
      setErrorsById(produce(errorsById, (errorsDraft) => {
        queryIds.forEach((id) => {
          errorsDraft[id] = result as ErrorResult;
        });
      }));
    } else {
      // If the status is not terminated, we want to check for errors returned at the individual query level and set
      // those. Also, we need to clear any existing errors associated with these ids if there is not an error in the
      // latest response. We deliberately copy the top level error object instead of using immer, because we want to set
      // new state for {@link useEventCache} and force a new render whether errors needed to be cleaned up or not.
      const newErrorsById = { ...errorsById };
      queryIds.forEach((id) => {
        const queryRequestResult = (result as SuccessResult)?.results?.find((r) => r.id === id);
        if (!queryRequestResult) {
          return;
        }
        const error = queryRequestResult.error;
        if (error) {
          // TODO: This is a hacky transformation to the ErrorResult type that the UI components expect. This type,
          // which is also used in useLRQ should probably be revisited when we move these to the top-level
          // {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-4398}
          newErrorsById[id] = {
            status: K.QueryStatus.Terminated,
            terminationReasons: [error.message || ''],
            terminationReasonsDetails: [error.details],
          };
        } else {
          delete newErrorsById[id];
        }
      });
      setErrorsById(newErrorsById);
    }
    setEventCacheWatcher({});
  }, result !== prevResult && result !== INITIAL_RESULT);

  return (
    <></>
  );
}

export default LRQRequestManager;
